import { Box, Card, Flex, Skeleton, VStack } from '@chakra-ui/react'
import { useLocation } from 'react-router-dom'
import LoadingTable from '../tables/base/LoadingTable'
import type { Column } from '../tables/base/MasterTable'

const peopleColumns: Column[] = [
  { label: '', accessor: 'select', type: 'select', width: '5%' },
  { label: 'Name', accessor: 'name', type: 'string', width: '24%' },
  { label: 'Job Title', accessor: 'current_position', type: 'string', width: '22%' },
  { label: 'Teams', accessor: 'teams', type: 'string', width: '20%' },
  { label: 'Country', accessor: 'country_of_work', type: 'string', width: '14%' },
  { label: 'Email', accessor: 'work_email', type: 'string', width: '15%' },
]

const teamColumns: Column[] = [
  { label: '', accessor: 'select', type: 'select', width: '5%' },
  { label: 'Team', accessor: 'name', type: 'string', width: '30%' },
  { label: 'Manager', accessor: 'team_manager', type: 'string', width: '25%' },
  { label: 'Members', accessor: 'members', type: 'string', width: '20%' },
  { label: 'Office', accessor: 'office', type: 'string', width: '20%' },
]

const documentColumns: Column[] = [
  { label: '', accessor: 'select', type: 'select', width: '5%' },
  { label: 'Title', accessor: 'title', type: 'string', width: '32%' },
  { label: 'Recipient', accessor: 'recipient', type: 'string', width: '20%' },
  { label: 'Status', accessor: 'status', type: 'string', width: '15%' },
  { label: 'Created', accessor: 'created_at', type: 'string', width: '14%' },
  { label: 'Version', accessor: 'version', type: 'string', width: '14%' },
]

const contentColumns: Column[] = [
  { label: '', accessor: 'select', type: 'select', width: '5%' },
  { label: 'Title', accessor: 'title', type: 'string', width: '30%' },
  { label: 'Type', accessor: 'type', type: 'string', width: '16%' },
  { label: 'Status', accessor: 'status', type: 'string', width: '16%' },
  { label: 'Author', accessor: 'author', type: 'string', width: '18%' },
  { label: 'Updated', accessor: 'updated_at', type: 'string', width: '15%' },
]

const candidateColumns: Column[] = [
  { label: '', accessor: 'select', type: 'select', width: '5%' },
  { label: 'Candidate', accessor: 'name', type: 'string', width: '28%' },
  { label: 'Vacancy', accessor: 'vacancy', type: 'string', width: '22%' },
  { label: 'Stage', accessor: 'stage', type: 'string', width: '16%' },
  { label: 'Owner', accessor: 'owner', type: 'string', width: '14%' },
  { label: 'Applied', accessor: 'created_at', type: 'string', width: '15%' },
]

const payrollColumns: Column[] = [
  { label: '', accessor: 'select', type: 'select', width: '5%' },
  { label: 'Name', accessor: 'name', type: 'string', width: '24%' },
  { label: 'Position', accessor: 'position', type: 'string', width: '22%' },
  { label: 'Salary', accessor: 'salary', type: 'string', width: '16%' },
  { label: 'Benefits', accessor: 'benefits', type: 'string', width: '18%' },
  { label: 'Country', accessor: 'country', type: 'string', width: '15%' },
]

const evaluationColumns: Column[] = [
  { label: 'Name', accessor: 'name', type: 'string', width: '26%' },
  { label: 'Teams', accessor: 'teams', type: 'string', width: '24%' },
  { label: 'Country', accessor: 'country_of_work', type: 'string', width: '14%' },
  { label: 'Status', accessor: 'status', type: 'string', width: '14%' },
  { label: 'Last Evaluation', accessor: 'created_at', type: 'string', width: '22%' },
]

const activityColumns: Column[] = [
  { label: 'Project', accessor: 'project', type: 'string', width: '28%' },
  { label: 'Activity', accessor: 'activity', type: 'string', width: '28%' },
  { label: 'Hours', accessor: 'hours', type: 'string', width: '14%' },
  { label: 'Date', accessor: 'date', type: 'string', width: '15%' },
  { label: 'Member', accessor: 'employee', type: 'string', width: '15%' },
]

const getColumnsForPath = (pathname: string): Column[] | null => {
  if (pathname.startsWith('/user/workforce/teams')) return teamColumns
  if (pathname.startsWith('/user/workforce')) return peopleColumns
  if (pathname.startsWith('/user/admin/document') || pathname.startsWith('/user/document')) return documentColumns
  if (pathname.startsWith('/user/content')) return contentColumns
  if (pathname.startsWith('/user/evaluations')) return evaluationColumns
  if (pathname.startsWith('/user/recruitment/interviews')) return candidateColumns
  if (pathname.startsWith('/user/recruitment/vacancy')) return candidateColumns
  if (pathname.startsWith('/user/recruitment')) return candidateColumns
  if (pathname.startsWith('/user/payroll')) return payrollColumns
  if (pathname.startsWith('/user/planning/time-tracking/activities')) return activityColumns
  if (pathname.startsWith('/user/planning/time-tracking/projects')) return payrollColumns
  if (pathname.startsWith('/user/planning')) return evaluationColumns
  return null
}

const isTableShellPath = (pathname: string) =>
  pathname.startsWith('/user/evaluations') ||
  pathname.startsWith('/user/payroll') ||
  pathname.startsWith('/user/admin/document') ||
  pathname.startsWith('/user/document') ||
  pathname.startsWith('/user/planning')

const renderTableShell = (columns: Column[]) => (
  <Card width="100%" p={{ base: '12px', md: '16px' }}>
    <VStack gap="12px" align="stretch" width="100%">
      <Flex
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        gap="12px"
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Skeleton height="40px" width={{ base: '100%', md: '280px' }} borderRadius="12px" />
        <Flex gap="8px" flexWrap="wrap">
          <Skeleton height="32px" width="124px" borderRadius="10px" />
          <Skeleton height="32px" width="112px" borderRadius="10px" />
          <Skeleton height="32px" width="96px" borderRadius="10px" />
        </Flex>
      </Flex>

      <LoadingTable columns={columns} rows={10} minHeight="58vh" />
    </VStack>
  </Card>
)

const RouteContentFallback = () => {
  const location = useLocation()
  const columns = getColumnsForPath(location.pathname)
  const tableShell = isTableShellPath(location.pathname)

  return (
    <Flex
      flexDirection={{ base: 'column', md: 'row' }}
      height="100%"
      overflowY="auto"
      borderRadius="0"
    >
      <VStack gap={{ base: '12px', md: '16px' }} width="100%">
        {!tableShell && (
          <Flex
            align="center"
            justify="space-between"
            width="100%"
            px={{ base: '0', md: '0' }}
            py="4px"
            gap="12px"
            flexWrap="wrap"
          >
            <Skeleton height="32px" width="220px" borderRadius="10px" />
            <Flex gap="8px" flexWrap="wrap">
              <Skeleton height="32px" width="110px" borderRadius="10px" />
              <Skeleton height="32px" width="110px" borderRadius="10px" />
              <Skeleton height="32px" width="96px" borderRadius="10px" />
            </Flex>
          </Flex>
        )}

        {columns ? (
          tableShell ? renderTableShell(columns) : <LoadingTable columns={columns} rows={10} minHeight="58vh" />
        ) : (
          <Box width="100%">
            <Skeleton height="88px" borderRadius="16px" mb="12px" />
            <Skeleton height="88px" borderRadius="16px" mb="12px" />
            <Skeleton height="88px" borderRadius="16px" />
          </Box>
        )}
      </VStack>
    </Flex>
  )
}

export default RouteContentFallback
